import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ErrorHelperService } from '@services/helpers/error-helper/error-helper.service';
import { PractitionerUserData } from '@models/data/auth/AuthPractitionerUserData';
import { Router } from '@angular/router';
import { AccountHttpService } from '@services/http/account/account-http.service';
import { firstValueFrom } from 'rxjs';
import { SafeResourceUrl } from '@angular/platform-browser';
import { ILoginFormData } from '@models/data/account/ILoginFormData';
import { LoadingService } from '@services/helpers/loading/loading.service';
import { RegionService } from '@src/app/areas/auth/services/region.service';
import {hasValue} from "@shared/utils/helpers";

type UserRole =
  | 'admin'
  | 'practitioner'
  | 'prescription-admin'
  | 'user-admin'
  | 'admin-billing'
  | 'knowledge-center-admin'
  | 'customer-support'
  | 'applications-reviewer'
  | 'translator';

@Injectable({ providedIn: 'root' })
export class AppAuthService extends LoadingService {
  public user$ = signal<PractitionerUserData | null>(null);

  private currentUserSubject = new BehaviorSubject<PractitionerUserData | null>(null);
  public currentUser: Observable<PractitionerUserData | null> = this.currentUserSubject.asObservable();

  private profilePictureSubject = new BehaviorSubject<SafeResourceUrl | null>(null);
  public profilePicture: Observable<SafeResourceUrl | null> = this.profilePictureSubject.asObservable();

  loggedIn$ = signal(false);

  constructor(
    private accountHttp: AccountHttpService,
    private errorHelperService: ErrorHelperService,
    private router: Router,
    private regionService: RegionService,
  ) {
    super();
    this.loading = true;
    this.refresh().then();
  }

  get isAdmin(): boolean {
    return this.hasRole('admin')
  }

  hasRole(roleName: UserRole): boolean {
    return !!this.currentUserValue?.identityProfile?.roles?.includes(roleName);
  }

  public set currentUserValue(content: PractitionerUserData | null) {
    console.log('currentUserValue user changed set current user val...', content);

    this.user$.set(content);
    this.currentUserSubject.next(content);

    // ✅ keep loggedIn$ in sync
    this.loggedIn$.set(!!content?.identityProfile);
  }

  public get currentUserValue(): PractitionerUserData | null {
    return this.currentUserSubject.getValue();
  }

  public get currentUserId(): string | null {
    return this.currentUserValue?.identityProfile?.externalIdentityId ?? null;
  }

  public get loggedIn(): boolean {
    return !!this.currentUserValue?.identityProfile;
  }

  public refreshUserPractitionerData(): void {
    console.log('refreshUserPractitionerData')
    this.loading = true;
    this.currentUserValue = null;
    this.refresh().then();
  }

  async login(formData: ILoginFormData): Promise<void> {
    this.refreshUserPractitionerData();
  }

  async logout(): Promise<void> {
    this.disposeCurrentUser();

    this.loading = true;
    const logoutResponse = await firstValueFrom(this.accountHttp.logout());
    this.loggedIn$.set(false);
    this.loading = false;
  }

  private disposeCurrentUser(): void {
    this.currentUserValue = null;
    this.regionService.reset()
  }

  private navigateToDashboard(): void {
    this.router.navigate(['/']).then();
  }

  private async refresh(): Promise<void> {
    this.loading = true;
    try {
      this.currentUserValue = await this.accountHttp.getCurrentUser();

      if (!hasValue(this.regionService.selectedRegion$())) {
        this.regionService.selectRegion(this.currentUserValue?.identityProfile?.region ?? null);
      }
    } catch (err: any) {
      console.log('refresh error getting data', err)
      this.currentUserValue = null;
      const status = err?.status ?? 500;
      if (status === 401) {
        this.errorHelperService.handleErr({ status, message: 'User is not logged in.' });
      }
    }

    if (this.router.url === '/account/login' && this.loggedIn) {
      await this.navigateToDashboard();
    }
    this.loading = false;
  }
}
